import React, { useEffect, useRef } from 'react';
import {
  MapContainer, TileLayer, FeatureGroup, Polygon, Polyline, Popup, ScaleControl, ZoomControl, useMap, useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
// leaflet-draw is a pre-ES-module plugin that hangs L.Draw off the global `L`
// leaflet itself installs on window. Imported for the side effect only; the
// drawing handlers below are created by hand, since the plugin's own toolbar
// is replaced by the buttons in the menus.
import 'leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import BaseLayers from '../BaseLayers.js';
import VelocityLayer from '../VelocityLayer.js';
import { useWorkspace } from './WorkspaceContext.js';
import {
  ACTION_TYPES,
  ACTION_META,
  BUILDING_STYLE,
  DISCHARGE_Z_INDEX,
  FLOW_PATHS_STATIC_Z_INDEX,
  VELOCITY_OPTIONS,
  WATER_LEVEL_Z_INDEX,
  formatWaterLevel,
  getDangerClassStyle,
} from './workspaceConstants.js';
import * as constants from '../constants.js';

const MAX_ZOOM = 21;

// Leaflet measures its container when the map is created, which can happen
// before the surrounding flex layout has settled. One re-measure after mount
// covers that; the menus no longer change the map's size, so nothing else
// needs to trigger it.
const ResizeAfterMount = () => {
  const map = useMap();
  useEffect(() => {
    const timeoutId = setTimeout(() => map.invalidateSize(), 0);
    return () => clearTimeout(timeoutId);
  }, [map]);
  return null;
};

// Drives Leaflet.Draw by hand: enables the handler matching the current
// drawing mode, reports finished shapes, and re-arms itself via `nonce` so
// several features can be drawn in a row.
const DrawHandler = ({ mode, nonce, onCreated, onCancel }) => {
  const map = useMap();
  const onCreatedRef = useRef(onCreated);
  const onCancelRef = useRef(onCancel);
  onCreatedRef.current = onCreated;
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!mode || !map || !L.Draw) return undefined;

    const geometry = mode === 'building' ? 'polygon' : ACTION_META[mode].geometry;
    const color = mode === 'building' ? BUILDING_STYLE.color : ACTION_META[mode].color;
    const shapeOptions = { shapeOptions: { color, weight: 4 } };
    const handler = geometry === 'polygon'
      ? new L.Draw.Polygon(map, shapeOptions)
      : new L.Draw.Polyline(map, shapeOptions);

    const handleCreated = (e) => {
      const { layer, layerType } = e;
      const latLngs = layerType === 'polygon' ? layer.getLatLngs()[0] : layer.getLatLngs();
      onCreatedRef.current(latLngs.map((latLng) => [latLng.lat, latLng.lng]));
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') onCancelRef.current();
    };

    map.on(L.Draw.Event.CREATED, handleCreated);
    document.addEventListener('keydown', handleEscape);
    handler.enable();

    return () => {
      map.off(L.Draw.Event.CREATED, handleCreated);
      document.removeEventListener('keydown', handleEscape);
      handler.disable();
    };
  }, [map, mode, nonce]);

  return null;
};

// Reports plain clicks on the map, for the water level popup. A click on a
// Maßnahme bubbles on to the map after the Maßnahme's own handler has opened
// its dialog; `featureClickRef` is set by that handler so the map ignores it.
const MapClickHandler = ({ onClick, featureClickRef }) => {
  useMapEvents({
    click: (e) => {
      if (featureClickRef.current) {
        featureClickRef.current = false;
        return;
      }
      onClick(e.latlng);
    },
  });
  return null;
};

const WorkspaceMap = () => {
  const {
    center,
    interactionMode,
    drawNonce,
    rearmDrawing,
    cancelInteraction,
    buildings,
    addBuilding,
    pendingBuildingDeletions,
    markBuildingForDeletion,
    actions,
    addAction,
    pendingActionDeletions,
    markActionForDeletion,
    openActionDialog,
    activeScenario,
    activeScenarioId,
    layers,
    dangerClasses,
    velocityData,
    resultsVersion,
    waterLevelProbe,
    probeWaterLevel,
    clearWaterLevelProbe,
  } = useWorkspace();

  const featureClickRef = useRef(false);

  const drawingKind = interactionMode && interactionMode.startsWith('draw-')
    ? interactionMode.slice('draw-'.length)
    : null;
  const erasingBuildings = interactionMode === 'erase-buildings';
  const erasingActions = interactionMode === 'erase-actions';

  const handleCreated = (points) => {
    if (drawingKind === 'building') {
      addBuilding(points);
    } else if (drawingKind) {
      addAction(drawingKind, points);
    }
    rearmDrawing();
  };

  // Buildings are always on the map while no scenario is active, and they stay
  // on while being drawn or erased, whatever the "Ansicht" toggle says -- the
  // user is working on them.
  const showBuildings = !activeScenarioId || layers.buildings
    || drawingKind === 'building' || erasingBuildings;
  const useDangerColours = Boolean(activeScenario) && activeScenario.status === 'done';

  const tileUrl = (endpoint) => `//${constants.BASE_URL}/${endpoint}/${activeScenarioId}/{z}/{x}/{y}.png?v=${resultsVersion}`;

  // Same for the Maßnahmen: while one is being drawn or erased, every type is
  // on the map regardless of the "Ansicht" toggles, so the user can see and
  // click what they are working on.
  const workingOnActions = erasingActions || Boolean(drawingKind && drawingKind !== 'building');
  // The water level can be queried by clicking the map once a computed
  // scenario is active and nothing is being drawn or erased.
  const canProbe = useDangerColours && !interactionMode;
  const handleMapClick = (latlng) => {
    if (canProbe) probeWaterLevel(latlng.lat, latlng.lng);
  };

  const visibleActionTypes = ACTION_TYPES.filter(
    (type) => workingOnActions || (layers.actions && layers.actionTypes[type])
  );

  return (
    <MapContainer
      center={[center.lat, center.long]}
      zoom={16}
      maxZoom={MAX_ZOOM}
      // Leaflet puts the zoom buttons top left by default, right on top of the
      // region title. Rendered by hand instead, after the base-map picker, so
      // Leaflet stacks it underneath in the same corner.
      zoomControl={false}
      style={{ height: '100%', width: '100%' }}
    >
      <BaseLayers maxZoom={MAX_ZOOM} />
      <ZoomControl position="topright" />
      <ScaleControl position="bottomleft" imperial={false} maxWidth={150} />
      <ResizeAfterMount />

      {activeScenarioId && layers.flowPathsAnimated && velocityData && (
        <VelocityLayer data={velocityData} options={VELOCITY_OPTIONS} />
      )}

      {activeScenarioId && layers.flowPathsStatic && (
        <TileLayer
          url={tileUrl('get_accumulation_mask_tile')}
          maxZoom={MAX_ZOOM}
          zIndex={FLOW_PATHS_STATIC_Z_INDEX}
          opacity={1}
          className="water-level-geotiff-overlay"
          crossOrigin="use-credentials"
          attribution="Rapid Resilience"
        />
      )}

      {activeScenarioId && layers.discharge && (
        <TileLayer
          url={tileUrl('get_discharge_tile')}
          maxZoom={MAX_ZOOM}
          zIndex={DISCHARGE_Z_INDEX}
          opacity={1}
          className="water-level-geotiff-overlay"
          crossOrigin="use-credentials"
          attribution="Rapid Resilience"
        />
      )}

      {activeScenarioId && layers.waterLevel && (
        <TileLayer
          url={tileUrl('get_water_level_tile')}
          maxZoom={MAX_ZOOM}
          zIndex={WATER_LEVEL_Z_INDEX}
          opacity={0.6}
          className="water-level-geotiff-overlay"
          crossOrigin="use-credentials"
          attribution="Rapid Resilience"
        />
      )}

      <FeatureGroup>
        {showBuildings && buildings
          .filter((building) => !pendingBuildingDeletions.includes(building.building_id))
          .map((building) => {
            const style = useDangerColours
              ? getDangerClassStyle(dangerClasses[building.building_id])
              : BUILDING_STYLE;
            return (
              <Polygon
                key={`building-${building.building_id}`}
                positions={building.building_outline}
                pathOptions={style}
                // A layer without a click listener is not an event target at
                // all in Leaflet, so outside the eraser mode the click travels
                // on to the map and Leaflet.Draw can place a vertex on top of
                // an existing building.
                eventHandlers={erasingBuildings
                  ? { click: () => markBuildingForDeletion(building.building_id) }
                  : undefined}
              />
            );
          })}

        {visibleActionTypes.map((type) => {
          const meta = ACTION_META[type];
          const Shape = meta.geometry === 'polygon' ? Polygon : Polyline;

          return actions[type]
            .filter((item) => !pendingActionDeletions.some(
              (entry) => entry.type === type && entry.id === item[meta.idKey]
            ))
            .map((item) => (
              <Shape
                key={`${type}-${item[meta.idKey]}`}
                positions={item[meta.geometryKey]}
                pathOptions={{ color: meta.color, weight: 4 }}
                eventHandlers={{
                  click: () => {
                    featureClickRef.current = true;
                    if (erasingActions) {
                      markActionForDeletion(type, item[meta.idKey]);
                    } else if (!interactionMode) {
                      openActionDialog(type, item[meta.idKey]);
                    }
                  },
                }}
              />
            ));
        })}
      </FeatureGroup>

      <MapClickHandler onClick={handleMapClick} featureClickRef={featureClickRef} />

      {canProbe && waterLevelProbe && (
        <Popup
          // One popup per clicked point, so each one's close handler refers
          // to its own position.
          key={`${waterLevelProbe.lat},${waterLevelProbe.lng}`}
          position={[waterLevelProbe.lat, waterLevelProbe.lng]}
          eventHandlers={{ remove: () => clearWaterLevelProbe(waterLevelProbe.lat, waterLevelProbe.lng) }}
        >
          {formatWaterLevel(waterLevelProbe)}
        </Popup>
      )}

      <DrawHandler
        mode={drawingKind}
        nonce={drawNonce}
        onCreated={handleCreated}
        onCancel={cancelInteraction}
      />
    </MapContainer>
  );
};

export default WorkspaceMap;
